import { styled } from "goober";
import { queries } from "../../../shared/queries";
import { useFocusShortcut } from "../../hooks/useFocusShortcut";
import { useQuery } from "../../hooks/useQuery";
import {
  makeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { ClearButton } from "../ClearButton";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { MainScrollContainer } from "../MainScrollContainer";
import { TabHeaderBar, Tabs } from "../TabHeader";
import { TabLink } from "../TabLink";

const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchRow = styled("div")`
  margin-left: auto;
  margin-right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchContainer = styled("div")`
  position: relative;
`;

const SearchIcon = styled("div")`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-primary);
  pointer-events: none;
`;

type Props = {
  children: React.ReactNode;
  searchAction?: React.ReactNode;
};

export function SkillsLayout({ children, searchAction }: Props) {
  const [isList] = useRouteMatch(routes.skills.list);
  const [isBrowse] = useRouteMatch(routes.skills.browse);
  const listUrl = useCurrentUrl(routes.skills.list);
  const browseUrl = useCurrentUrl(routes.skills.browse);
  const query = (isBrowse ? browseUrl.q : listUrl.q) ?? "";
  const searchRef = useFocusShortcut<HTMLInputElement>();

  const [skills, skillsDetails] = useQuery(queries.skillsIndex());
  const count = skills.length;

  const setQuery = (q: string) => {
    if (isBrowse) {
      navigate(routes.skills.browse, { q }, { replace: true });
    } else {
      navigate(routes.skills.list, { q }, { replace: true });
    }
  };

  return (
    <Backdrop permanentNavigation>
      <Container>
        <TabHeaderBar>
          <Tabs>
            <TabLink
              to={makeUrl(routes.skills.list, { q: "" })}
              active={isList}
              icon="graduation-cap"
              label="Skills"
              count={skillsDetails.type === "complete" ? count : null}
            />
            <TabLink
              to={makeUrl(routes.skills.browse, { q: "" })}
              active={isBrowse}
              icon="store"
              label="Browse templates"
            />
          </Tabs>
          <SearchRow>
            <SearchContainer>
              <SearchIcon>
                <Icon name="search" size={12} />
              </SearchIcon>
              <Input
                ref={searchRef}
                data-search
                placeholder="Search…"
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <ClearButton
                  onPress={() => {
                    setQuery("");
                    searchRef.current?.focus();
                  }}
                >
                  <Icon name="x" size={12} />
                </ClearButton>
              )}
            </SearchContainer>
            {searchAction}
          </SearchRow>
        </TabHeaderBar>

        <MainScrollContainer>{children}</MainScrollContainer>
      </Container>
    </Backdrop>
  );
}
